import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { DataTable, ExportCsvButton, FilterBar, Section, StatCard } from "@/components/admin/admin-kit";
import { admSegment } from "@/lib/admin-pro.functions";
import type { SegmentQuery } from "@/lib/admin-pro.schemas";
import { formatMoney } from "@/lib/behot";
import { privatePageMetadata } from "@/lib/seo";

export const Route = createFileRoute("/nc/live/adm/segments")({
  head: () => privatePageMetadata("/nc/live/adm/segments", "Segments | Control room", "Internal user segments."),
  component: AdminSegments,
});

const SEGMENTS: { value: SegmentQuery["segment"]; label: string }[] = [
  { value: "admins", label: "Admins" },
  { value: "moderators", label: "Moderators" },
  { value: "pros", label: "Professionals" },
  { value: "ambassadors", label: "Ambassadors" },
  { value: "sellers", label: "Sellers" },
  { value: "referrers", label: "Referrers" },
  { value: "spenders", label: "Top spenders" },
  { value: "earners", label: "Earners" },
  { value: "suspended", label: "Suspended" },
  { value: "dormant", label: "Dormant" },
];

function AdminSegments() {
  const run = useServerFn(admSegment);
  const [segment, setSegment] = useState<SegmentQuery["segment"]>("pros");

  const { data, isLoading } = useQuery({
    queryKey: ["adm-segment", segment],
    queryFn: () => run({ data: { segment, limit: 200 } }),
  });

  const counts = data?.counts;

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <StatCard label="Total users" value={counts?.users ?? "—"} tone="hot" />
        <StatCard label="Professionals" value={counts?.pros ?? "—"} />
        <StatCard label="Sellers" value={counts?.sellers ?? "—"} />
        <StatCard label="Earners" value={counts?.earners ?? "—"} tone="ok" />
        <StatCard label="Suspended" value={counts?.suspended ?? "—"} tone="warn" />
      </div>

      <Section
        title="User segments"
        description="Slice the audience for moderation, outreach and reporting."
        actions={
          <ExportCsvButton rows={(data?.rows ?? []) as unknown as Record<string, unknown>[]} filename={`behot-${segment}`} />
        }
      >
        <FilterBar>
          {SEGMENTS.map((s) => (
            <button
              key={s.value}
              type="button"
              onClick={() => setSegment(s.value)}
              className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                segment === s.value
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border text-muted-foreground hover:bg-muted"
              }`}
            >
              {s.label}
            </button>
          ))}
        </FilterBar>

        <DataTable
          rows={data?.rows ?? []}
          rowKey={(r) => r.id}
          empty={isLoading ? "Loading…" : "No users in this segment."}
          columns={[
            {
              key: "user",
              header: "User",
              cell: (r) => (
                <div>
                  <p className="font-medium">{r.display_name ?? r.handle ?? r.id.slice(0, 8)}</p>
                  {r.slug ? (
                    <Link to="/user/$slug" params={{ slug: r.slug }} search={{ ref: undefined }} className="text-xs text-primary hover:underline">
                      /user/{r.slug}
                    </Link>
                  ) : null}
                </div>
              ),
              sortValue: (r) => r.display_name ?? "",
            },
            {
              key: "flags",
              header: "Flags",
              cell: (r) => (
                <div className="flex flex-wrap gap-1">
                  <Badge variant="outline">{r.account_mode}</Badge>
                  {r.pro_enabled ? <Badge>Pro</Badge> : null}
                  {r.is_ambassador ? <Badge variant="secondary">Ambassador</Badge> : null}
                  {r.is_suspended ? <Badge variant="destructive">Suspended</Badge> : null}
                </div>
              ),
            },
            {
              key: "wallets",
              header: "Wallets",
              cell: (r) => (
                <span className="text-xs text-muted-foreground">
                  Top-up {formatMoney(r.wallet?.topup_balance_cents ?? 0)} · Earn{" "}
                  {formatMoney(r.wallet?.earn_balance_cents ?? 0)} · Reward{" "}
                  {formatMoney(r.wallet?.referral_balance_cents ?? 0)}
                </span>
              ),
              sortValue: (r) => r.wallet?.earn_balance_cents ?? 0,
            },
            {
              key: "created",
              header: "Joined",
              cell: (r) => new Date(r.created_at).toLocaleDateString(),
              sortValue: (r) => r.created_at,
            },
          ]}
        />
      </Section>
    </div>
  );
}
